import { useCallback, useRef, useState } from 'react';
import type { Annotation } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import { UNDO_STACK_LIMIT } from '@v2/components/imageHandling/components/DrawingCanvas/constants';

export interface UseAnnotationUndoRedoParams {
  annotations: Annotation[];
  setAnnotations: (annotations: Annotation[]) => void;
}

export const useAnnotationUndoRedo = ({ annotations, setAnnotations }: UseAnnotationUndoRedoParams) => {
  const [undoStack, setUndoStack] = useState<Annotation[][]>([]);
  const [redoStack, setRedoStack] = useState<Annotation[][]>([]);
  const annotationsRef = useRef<Annotation[]>(annotations);
  annotationsRef.current = annotations;

  const pushUndoSnapshot = useCallback(() => {
    setUndoStack((prev) => {
      const next = [...prev, [...annotationsRef.current]];
      return next.length > UNDO_STACK_LIMIT ? next.slice(next.length - UNDO_STACK_LIMIT) : next;
    });
    setRedoStack([]);
  }, []);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const previous = prev[prev.length - 1];
      setRedoStack((r) => [...r, [...annotationsRef.current]]);
      setAnnotations(previous);
      return prev.slice(0, -1);
    });
  }, [setAnnotations]);

  const redo = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const next = prev[prev.length - 1];
      setUndoStack((u) => [...u, [...annotationsRef.current]]);
      setAnnotations(next);
      return prev.slice(0, -1);
    });
  }, [setAnnotations]);

  return {
    undo,
    redo,
    pushUndoSnapshot,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
  };
};
